import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useGlobalSearch, SearchResult } from "@/hooks/useGlobalSearch";
import { CalendarPlus, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUserClinicIds } from "@/hooks/useUserClinics";
import { useUpdateAppointment } from "@/hooks/useAppointments";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CreateAppointmentDialog } from "@/components/appointments/CreateAppointmentDialog";

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RECENT_SEARCHES_KEY = "global-search-recent";
const MAX_RECENT_SEARCHES = 5;

export const GlobalSearchDialog = ({ open, onOpenChange }: GlobalSearchDialogProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>([]);
  const { results, isLoading } = useGlobalSearch({ query, enabled: open });
  const { clinicIds, clinics } = useUserClinicIds();
  const updateAppointment = useUpdateAppointment();

  // State for appointment booking
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [selectedPatientForAction, setSelectedPatientForAction] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const primaryClinicId = clinics[0]?.clinic_id;

  // Load and sanitize recent searches from localStorage (remove any cached phone numbers)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        const searches = JSON.parse(stored);
        // Sanitize: remove phone number patterns from subtitles (10 digits or +91 format)
        const sanitized = searches
          .map((s: SearchResult) => ({
            ...s,
            subtitle: s.subtitle?.match(/^(\+91\s?)?\d{10}$/) ? "View patient profile" : s.subtitle
          }))
          .filter((s: SearchResult) => s.id && s.title && s.route && s.registrationNumber);
        // Update localStorage with sanitized data
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(sanitized));
        setRecentSearches(sanitized);
      }
    } catch {
      // Ignore parse errors
    }
  }, [open]);

  // Save to recent searches
  const saveToRecent = useCallback((result: SearchResult) => {
    setRecentSearches((prev) => {
      const filtered = prev.filter((r) => r.id !== result.id);
      const updated = [result, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Create a lookup map for results
  const resultMap = useMemo(() => {
    const map = new Map<string, SearchResult>();
    results.forEach((result) => {
      map.set(`${result.type}-${result.id}`, result);
    });
    recentSearches.forEach((result) => {
      map.set(`recent-${result.type}-${result.id}`, result);
    });
    return map;
  }, [results, recentSearches]);

  // Handle selection by value string (for cmdk compatibility)
  const handleSelectByValue = useCallback(
    (value: string) => {
      const result = resultMap.get(value);
      if (result) {
        saveToRecent(result);
        navigate(result.route);
        onOpenChange(false);
        setQuery("");
      }
    },
    [resultMap, navigate, onOpenChange, saveToRecent]
  );

  // Book appointment handler
  const handleBookAppointment = useCallback((e: React.MouseEvent, patientId: string, patientName: string) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedPatientForAction({ id: patientId, name: patientName });
    onOpenChange(false);
    setQuery("");
    // Small delay to allow search dialog to close first
    setTimeout(() => {
      setAppointmentDialogOpen(true);
    }, 100);
  }, [onOpenChange]);

  // Check-in handler
  const handleQuickCheckIn = useCallback(async (e: React.MouseEvent, patientId: string, patientName: string) => {
    e.stopPropagation();
    e.preventDefault();

    if (clinicIds.length === 0) {
      toast.error("No clinic access available");
      return;
    }

    try {
      // Get today's date range
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Find today's appointment for this patient
      const { data: todayAppointment, error } = await supabase
        .from("appointments")
        .select("id, status, start_time")
        .eq("patient_id", patientId)
        .in("clinic_id", clinicIds)
        .gte("start_time", today.toISOString())
        .lt("start_time", tomorrow.toISOString())
        .in("status", ["scheduled", "confirmed"])
        .order("start_time", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (todayAppointment) {
        // Check them in
        await updateAppointment.mutateAsync({
          id: todayAppointment.id,
          status: "checked_in"
        });
        toast.success(`${patientName} checked in successfully`);
        onOpenChange(false);
        setQuery("");
      } else {
        toast.info("No appointment found today. Navigate to patient profile to create a walk-in.");
      }
    } catch (error) {
      console.error("Check-in error:", error);
      toast.error("Failed to check in patient");
    }
  }, [clinicIds, updateAppointment, onOpenChange]);

  // Register keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  // Helper: get initials from name
  const getInitials = (name: string) => {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  // Helper: calculate age from DOB
  const calculateAge = (dob: string) => {
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  // Metadata chips for a result
  const ResultMetadata = ({ result }: { result: SearchResult }) => (
    <div className="flex items-center gap-1.5 flex-wrap">
      {result.registrationNumber && (
        <Badge variant="outline" size="sm" className="text-[10px] font-normal">
          {result.registrationNumber}
        </Badge>
      )}
      {result.dateOfBirth && (
        <span className="text-[11px] text-muted-foreground">{calculateAge(result.dateOfBirth)} yrs</span>
      )}
      {result.gender && (
        <span className="text-[11px] text-muted-foreground capitalize">{result.gender}</span>
      )}
    </div>
  );

  // Patient avatar
  const PatientResultAvatar = ({ name }: { name: string }) => (
    <Avatar className="h-8 w-8 mr-2.5 shrink-0">
      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
  );

  // Quick action buttons component
  const QuickActionButtons = ({ patientId, patientName }: { patientId: string; patientName: string }) => (
    <div className="flex items-center gap-1 ml-2">
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 hover:bg-green-100 dark:hover:bg-green-900/30"
              onClick={(e) => handleBookAppointment(e, patientId, patientName)}
            >
              <CalendarPlus className="h-4 w-4 text-green-600" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Book Appointment</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 hover:bg-blue-100 dark:hover:bg-blue-900/30"
              onClick={(e) => handleQuickCheckIn(e, patientId, patientName)}
            >
              <UserCheck className="h-4 w-4 text-blue-600" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Check In</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  return (
    <>
      <CommandDialog open={open} onOpenChange={onOpenChange}>
        <CommandInput
          placeholder="Search patients by name, phone, email..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {isLoading && query.length >= 2 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Searching...
            </div>
          )}

          {!isLoading && query.length >= 2 && results.length === 0 && (
            <CommandEmpty>No patients found.</CommandEmpty>
          )}

          {/* Recent searches when no query */}
          {query.length < 2 && recentSearches.length > 0 && (
            <CommandGroup heading="Recent Searches">
              {recentSearches.map((result) => (
                <CommandItem
                  key={`recent-${result.id}`}
                  value={`recent-${result.type}-${result.id}`}
                  onSelect={handleSelectByValue}
                  className="cursor-pointer py-2.5"
                >
                  <PatientResultAvatar name={result.title} />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium truncate">{result.title}</span>
                    <ResultMetadata result={result} />
                  </div>
                  <QuickActionButtons patientId={result.id} patientName={result.title} />
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Show hint when no query */}
          {query.length < 2 && recentSearches.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Type at least 2 characters to search...
            </div>
          )}

          {/* Patient search results */}
          {!isLoading && results.length > 0 && (
            <CommandGroup heading="Patients">
              {results.map((result) => (
                <CommandItem
                  key={result.id}
                  value={`${result.type}-${result.id}`}
                  onSelect={handleSelectByValue}
                  className="cursor-pointer py-2.5"
                >
                  <PatientResultAvatar name={result.title} />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium truncate">{result.title}</span>
                    <ResultMetadata result={result} />
                  </div>
                  <QuickActionButtons patientId={result.id} patientName={result.title} />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>

      {/* Appointment Dialog */}
      {selectedPatientForAction && primaryClinicId && (
        <CreateAppointmentDialog
          open={appointmentDialogOpen}
          onOpenChange={(open) => {
            setAppointmentDialogOpen(open);
            if (!open) {
              setSelectedPatientForAction(null);
            }
          }}
          clinicId={primaryClinicId}
          preSelectedPatient={{
            id: selectedPatientForAction.id,
            name: selectedPatientForAction.name,
          }}
        />
      )}
    </>
  );
};
